import { RenderImageType } from "client/Api/AppIcon/common";
import { BusyIndicator } from "client/Components";
import {
	AlertManager,
	useAlerts,
	useExternalJsScript,
	useIsMounted
} from "client/Hooks";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { FrequentlyUsedTileVisual } from "../../FrequentlyUsedTileGroup/FrequentlyUsedTileVisual";
import { FrequentlyUsedTileVisualList } from "../../FrequentlyUsedTileGroup/FrequentlyUsedTileVisualList";
import { LiveTileContent } from "../LiveTileContent";
import styles from "./LiquitLiveTileContent.module.less";
import {
	getConnectionInformation,
	LiquitConnectionInformationModel
} from "client/Api/Workspace/LiveInformation/Liquit/liquitLiveInformation";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { TileContext } from "../TileContext";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type LiquitLiveTileContentProps = {
	isConfiguredCorrectly: boolean;
	onNavigation(): void;
	connectionId: number;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const LiquitLiveTileContent = (props: LiquitLiveTileContentProps): ReactElement => {
	const {
		isConfiguredCorrectly,
		onNavigation,
		connectionId,
		onOpenTileMenu
	} = props;

	const alertManager: AlertManager = useAlerts();
	const [loadingState, setLoadingState] = useState<LoadingState>({
		kind: "loading"
	});
	const [searchTerm, setSearchTerm] = useState<string>("");

	const isMounted: () => boolean = useIsMounted();

	const {
		name,
		icon,
		color,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const didScriptLoadSuccessfully: boolean | undefined = useExternalJsScript(
		"https://api.liquit.com/workspace/v1/liquit.workspace.js"
	);

	useEffect(
		(): void => {
			if (!isConfiguredCorrectly) {
				setLoadingState({
					kind: "failure"
				});

				return;
			}

			if (!didScriptLoadSuccessfully) {
				setLoadingState({
					kind: "loading"
				});

				return;
			}

			const loadApps = async (): Promise<void> => {
				setLoadingState({
					kind: "loading"
				});

				let connectionInformation: LiquitConnectionInformationModel;
				try {
					connectionInformation = await getConnectionInformation(connectionId);
				} catch (e) {
					console.error("Failed to load connection information", e);
					if (isMounted()) {
						setLoadingState({
							kind: "failure"
						});
					}

					return;
				}

				const {
					portalUrl,
					identitySource,
					accessToken,
					userPrincipalName
				} = connectionInformation;

				const signInThenLoadApps = (): void => {
					Liquit.SSO(
						{
							token: accessToken,
							identitySource: identitySource,
							username: userPrincipalName
						},
						(_, fault) => {
							if (!isMounted()) {
								return;
							}

							if (isNotNil(fault)) {
								console.error("Failed to authenticate with liquit", fault);
								setLoadingState({
									kind: "failure"
								});
								return;
							}

							loadApplications();
						}
					);
				};

				const loadApplications = (): void => {
					Liquit.Applications.List(
						{
							sort: ListSort.UserDefined
						},
						(fault, result) => {
							if (!isMounted()) {
								return;
							}

							if (isNotNil(fault)) {
								console.error("Failed to list application from liquit", fault);
								setLoadingState({
									kind: "failure"
								});
								return;
							}

							setLoadingState({
								kind: "success",
								items: result
							});
						}
					);
				};

				// NOTE: liquit's api is pretty bad in providing errors. I.e. if the app is configured incorrectly or
				// something unexpected happens, the callback is not called nor is an Error throw, it just shallows it
				Liquit.Initialize(portalUrl, {});

				signInThenLoadApps();

			};

			loadApps();
		},
		[
			alertManager,
			didScriptLoadSuccessfully,
			isMounted,
			isConfiguredCorrectly,
			connectionId
		]
	);

	const launchApp = useCallback(
		(app: Liquit.Application): void => {
			if (!app.enabled) {
				throw new Error("Cannot launch a disabled app");
			} else {
				Liquit.Applications
					.Launch(
						app.id,
						null,
						(args) => {
							switch (args.state) {
								case "success":
									break;
								case "failure":
									alertManager.showError(localize("_GeneralErrorMessage"));
									break;
								default:
									break;
							}
						}
					);
			}
		},
		[alertManager]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigation();
		},
		[addAnalyticsEvent, onNavigation]
	);

	const clickHandler = useCallback(
		(item: Liquit.Application): void => {
			addAnalyticsEvent();

			if (item.enabled) {
				launchApp(item);
			}
		},
		[addAnalyticsEvent, launchApp]
	);

	const renderContent = (): ReactElement | null => {
		if (!isConfiguredCorrectly) {
			return <LiveTileContent.Message text={localize("_IncompleteConfigurationPleaseContactYourAdmin")} />;
		}

		switch (loadingState.kind)
		{
			case "loading":
				return null;
			case "failure":
				return <LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />;
			case "success":
				if (loadingState.items.length === 0)
				{
					return <LiveTileContent.Message text={localize("_NoItemsToShow")} />;
				}

				let appsToRender: readonly Liquit.Application[] = loadingState.items;
				if (searchTerm) {
					appsToRender = appsToRender.filter(
						x => x.name
							?.toUpperCase()
							.includes(
								searchTerm.toUpperCase()
							)
					);
				}

				if (appsToRender.length === 0) {
					return <LiveTileContent.Message text={localize("_NoSearchResults")} />;
				}

				return (
					<LiveTileContent.Scrollable>
						<div className={styles.list}>
							<FrequentlyUsedTileVisualList>
								{appsToRender.map(
									a => (
										<FrequentlyUsedTileVisualList.Item key={a.id}>
											<FrequentlyUsedTileVisual
												trackingId={generateTrackingId("liquit-app", `|${a.id}`)}
												name={a.name}
												color={color}
												icon={{
													imageUrl: a.icon,
													renderType: RenderImageType.Raster
												}}
												isDisabled={!a.enabled}
												onClick={() => clickHandler(a)}
												tooltip={a.enabled ? undefined : localize("_NotAvailable")}
											/>
										</FrequentlyUsedTileVisualList.Item>
									)
								)}
							</FrequentlyUsedTileVisualList>
						</div>
					</LiveTileContent.Scrollable>
				);
			default:
				never(loadingState);
		}

	};

	return (
		<LiveTileContent
			name={name}
			icon={icon}
			overlay={overlayState}
			onOpenTileMenu={onOpenTileMenu}
			overlayStatusIcon={<TileStatus />}
		>
			<LiveTileContent.Header icon={icon}>
				<LiveTileContent.Title text={localize("_RecastApps")} />
			</LiveTileContent.Header>

			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.SearchInput
						onChange={setSearchTerm}
						placeholder={localize("_Search")}
					/>
					<LiveTileContent.BusyIndicatorContainer>
						{loadingState.kind === "loading" && (
							<BusyIndicator />
						)}

						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={name}
				onClick={navigateHandler}
			/>
		</LiveTileContent>
	);
};
LiquitLiveTileContent.displayName = "LiquitLiveTileContent";

type LoadingState =
	{ kind: "loading" | "failure"; }
	|
	{
		kind: "success";
		items: readonly Liquit.Application[];
	};

enum ListSort {
	UserDefined = 0,
	NameAscending = 1,
	NameDescending = 2,
	LaunchCount = 3,
	LastLaunch = 4
}
